import { ClipboardList, History, Loader2, MoreVertical } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useCreateOpenFileTaskMutation,
  useMergeCoveragesMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageSource,
  CoverageStatus,
} from '@/services/types/coverage-types';

interface CoverageCardDropdownMenuProps {
  coverage: CoverageListItem;
  matterId: string;
  isReadOnly: boolean;
  onClick?: () => void;
  allCoverages?: CoverageListItem[];
  onMerge?: (destinationId: string, sourceId: string) => void;
  onActivityOpen: () => void;
  onDeleteOpen: () => void;
}

export const CoverageCardDropdownMenu = ({
  coverage,
  matterId,
  isReadOnly,
  onClick,
  allCoverages,
  onMerge,
  onActivityOpen,
  onDeleteOpen,
}: CoverageCardDropdownMenuProps) => {
  const navigate = useNavigate();
  const [updateCoverage] = useUpdateCoverageMutation();
  const [mergeCoverages] = useMergeCoveragesMutation();
  const [createOpenFileTask] = useCreateOpenFileTaskMutation();
  const [isCreatingOpenFile, setIsCreatingOpenFile] = useState(false);

  const isHealthInsurance =
    coverage.category === CoverageCategory.FIRST_PARTY_HEALTH;
  const isApproved = coverage.status === CoverageStatus.APPROVED;
  const canGenerateOpenFile = !isReadOnly && isHealthInsurance && isApproved;

  const handleEdit = () => {
    navigate(`/matters/${matterId}/coverage/${coverage.id}/edit`);
  };

  const handleMergeSelect = async (destinationId: string, sourceId: string) => {
    try {
      await mergeCoverages({
        matterId,
        mergeCoverageRequest: {
          dst_coverage_id: destinationId,
          src_coverage_ids: [sourceId],
        },
      }).unwrap();
      toast.success('Coverages merged');
    } catch {
      toast.error('Merge failed');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-0.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 rounded"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="font-medium w-44">
        {isReadOnly && coverage.source === CoverageSource.SHARED ? (
          <DropdownMenuItem
            onClick={() => navigate(`/matters/${coverage.primary_matter_id}/coverage`)}
            className="text-xs"
          >
            View on primary case
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={onClick} className="text-xs">
            View details
          </DropdownMenuItem>
        )}
        {!isReadOnly && (
          <DropdownMenuItem onClick={handleEdit} className="text-xs">
            Edit
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onActivityOpen}
          className="text-xs"
        >
          <History className="h-3.5 w-3.5 mr-1.5" />
          View activity
        </DropdownMenuItem>
        {!isReadOnly && coverage.status !== CoverageStatus.APPROVED && (
          <DropdownMenuItem
            className="text-xs"
            onClick={async () => {
              try {
                await updateCoverage({
                  matterId,
                  coverageId: coverage.id,
                  coverage: { status: CoverageStatus.APPROVED },
                }).unwrap();
                toast.success('Coverage approved');
              } catch {
                toast.error('Failed to approve coverage');
              }
            }}
          >
            Approve
          </DropdownMenuItem>
        )}
        {!isReadOnly && coverage.status !== CoverageStatus.REJECTED &&
          coverage.status !== CoverageStatus.NOT_APPLICABLE && (
            <DropdownMenuItem
              className="text-xs"
              onClick={async () => {
                try {
                  await updateCoverage({
                    matterId,
                    coverageId: coverage.id,
                    coverage: { status: CoverageStatus.REJECTED },
                  }).unwrap();
                  toast.success('Coverage rejected');
                } catch {
                  toast.error('Failed to reject coverage');
                }
              }}
            >
              Reject
            </DropdownMenuItem>
          )}
        {!isReadOnly && coverage.status !== CoverageStatus.NOT_APPLICABLE && (
          <DropdownMenuItem
            className="text-xs"
            onClick={async () => {
              try {
                await updateCoverage({
                  matterId,
                  coverageId: coverage.id,
                  coverage: { status: CoverageStatus.NOT_APPLICABLE },
                }).unwrap();
                toast.success('Coverage marked as N/A');
              } catch {
                toast.error('Failed to update coverage');
              }
            }}
          >
            Mark as N/A
          </DropdownMenuItem>
        )}
        {!isReadOnly && (allCoverages ?? []).length > 1 && (
          <DropdownMenuItem
            className="text-xs"
            onClick={async () => {
              const newPrimary = !coverage.is_primary;
              try {
                await updateCoverage({
                  matterId,
                  coverageId: coverage.id,
                  coverage: { is_primary: newPrimary },
                }).unwrap();
                toast.success(
                  newPrimary
                    ? 'Coverage marked as primary'
                    : 'Primary status removed',
                );
              } catch {
                toast.error('Failed to update primary status');
              }
            }}
          >
            {coverage.is_primary
              ? 'Remove primary'
              : 'Mark as primary'}
          </DropdownMenuItem>
        )}
        {canGenerateOpenFile && (
          <DropdownMenuItem
            className="text-xs"
            disabled={isCreatingOpenFile}
            onClick={async () => {
              setIsCreatingOpenFile(true);
              try {
                await createOpenFileTask({
                  matterId,
                  coverageId: coverage.id,
                }).unwrap();
                toast.success('Open File task created');
              } catch (error) {
                const status = (error as { status?: number })?.status;
                if (status === 409) {
                  toast.info(
                    'Open File task already exists for this coverage',
                  );
                } else {
                  toast.error('Failed to create Open File task');
                }
              } finally {
                setIsCreatingOpenFile(false);
              }
            }}
          >
            {isCreatingOpenFile ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            )}
            {isCreatingOpenFile
              ? 'Generating...'
              : 'Generate Open File Task'}
          </DropdownMenuItem>
        )}
        {!isReadOnly && (onMerge || matterId) && (allCoverages ?? []).filter((c) => c.id !== coverage.id && c.category === coverage.category).length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-xs">
              Merge
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64">
              {(allCoverages ?? [])
                .filter((c) => c.id !== coverage.id)
                .filter((c) => c.category === coverage.category)
                .map((c) => (
                  <DropdownMenuItem
                    key={c.id}
                    className="text-xs"
                    onClick={() => {
                      if (onMerge) {
                        onMerge(c.id, coverage.id);
                      } else {
                        handleMergeSelect(c.id, coverage.id);
                      }
                    }}
                  >
                    {c.category === CoverageCategory.PROPERTY
                      ? (c.named_insured?.[0] || c.carrier?.name || 'Unknown Property')
                      : (c.carrier?.name || 'Unknown Carrier')}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {!isReadOnly && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDeleteOpen}
              className="text-red-600 focus:text-red-600 focus:bg-red-50 text-xs"
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
